import { useEffect, useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { VariableAutocompleteTextarea } from './VariableAutocompleteTextarea'
import { SelectableBox } from '@/components/ui/selectable-box'
import { Switch } from '@/components/ui/switch'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Separator } from '@/components/ui/separator'
import { ChevronDown, Wrench } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  BUILT_IN_TOOLS,
  SCHEDULING_STRATEGY_OPTIONS,
  type AgentDirection,
  type AgentChannel,
} from '@super/shared-types'
import type { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import type { FormValues } from './agent-detail.schema'

// ---------------------------------------------------------------------------
// Skill registry — add new skills here as they become available.
// gatedBy: integration type string that must be connected for this skill to show.
// ---------------------------------------------------------------------------
const AVAILABLE_SKILLS: {
  type: string
  label: string
  description: string
  gatedBy?: string
}[] = [
  {
    type: 'SCHEDULING',
    label: 'Schedule showing',
    description: 'Schedule property showings directly during a call',
    gatedBy: 'SHOWMOJO',
  },
  {
    type: 'OWNER_MEETING_SCHEDULING',
    label: 'Schedule owner meeting',
    description: 'Book a meeting with an owner lead during a call',
    gatedBy: 'HUBSPOT',
  },
]

const SKILL_DEFAULT_PARAMS: Record<string, Record<string, unknown>> = {
  SCHEDULING: { strategy: 'FIRST_AVAILABLE', backupLink: null },
  OWNER_MEETING_SCHEDULING: { meetingLinkSlug: null },
}

type ToolsCardProps = {
  control: Control<FormValues>
  watch: UseFormWatch<FormValues>
  setValue: UseFormSetValue<FormValues>
  direction: AgentDirection
  channels: AgentChannel[]
  connectedIntegrationTypes: string[]
  ownerLeadMeetingSkillEnabled: boolean
}

export function ToolsCard({
  control,
  watch,
  setValue,
  direction,
  connectedIntegrationTypes,
  ownerLeadMeetingSkillEnabled,
}: ToolsCardProps) {
  // -------------------------------------------------------------------------
  // Tools
  // -------------------------------------------------------------------------
  const visibleTools = useMemo(
    () =>
      BUILT_IN_TOOLS.filter(
        t => t.direction === 'BOTH' || t.direction === direction,
      ),
    [direction],
  )

  const visibleToolIds = useMemo(
    () => new Set(visibleTools.map(t => t.id)),
    [visibleTools],
  )

  const enabledTools = watch('enabledTools') ?? []

  useEffect(() => {
    const filtered = enabledTools.filter(id => visibleToolIds.has(id))
    if (filtered.length !== enabledTools.length) {
      setValue('enabledTools', filtered, { shouldDirty: true })
    }
  }, [direction, enabledTools, visibleToolIds, setValue])

  // -------------------------------------------------------------------------
  // Skills
  // -------------------------------------------------------------------------
  const visibleSkills = useMemo(
    () =>
      AVAILABLE_SKILLS.filter(skill => {
        if (
          skill.gatedBy &&
          !connectedIntegrationTypes.includes(skill.gatedBy)
        ) {
          return false
        }
        if (
          skill.type === 'OWNER_MEETING_SCHEDULING' &&
          !ownerLeadMeetingSkillEnabled
        ) {
          return false
        }
        return true
      }),
    [connectedIntegrationTypes, ownerLeadMeetingSkillEnabled],
  )

  const skills = watch('skills') ?? []

  const getSkillEntry = (type: string) =>
    skills.find(s => s.type === type) ?? null

  const toggleSkill = (type: string, currentlyEnabled: boolean) => {
    setValue(
      'skills',
      currentlyEnabled
        ? skills.filter(s => s.type !== type)
        : [...skills, { type, params: SKILL_DEFAULT_PARAMS[type] ?? {} }],
      { shouldDirty: true },
    )
  }

  const updateSkillParam = (type: string, key: string, value: unknown) => {
    setValue(
      'skills',
      skills.map(s =>
        s.type === type
          ? { ...s, params: { ...(s.params ?? {}), [key]: value } }
          : s,
      ),
      { shouldDirty: true },
    )
  }

  // Tracks which skill config panels are expanded in the UI.
  // Enabling a skill auto-expands it; disabling auto-collapses it.
  // The header row can also collapse/expand independently while the skill stays on.
  const [expandedSkills, setExpandedSkills] = useState<Set<string>>(new Set())

  const handleToggleSkill = (type: string, currentlyEnabled: boolean) => {
    toggleSkill(type, currentlyEnabled)
    setExpandedSkills(prev => {
      const next = new Set(prev)
      if (currentlyEnabled) next.delete(type)
      else next.add(type)
      return next
    })
  }

  const handleToggleExpanded = (type: string) => {
    setExpandedSkills(prev => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return next
    })
  }

  const showTools = visibleTools.length > 0
  const showSkills = visibleSkills.length > 0

  if (!showTools && !showSkills) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Wrench className="w-4 h-4" />
          Tools &amp; skills
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {showTools && (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Tools
            </p>
            <FormField
              control={control}
              name="enabledTools"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div
                      className="space-y-2"
                      role="group"
                      aria-label="Enabled tools"
                    >
                      {visibleTools.map(tool => {
                        const enabled = (field.value ?? []).includes(tool.id)
                        const isVoicemail = tool.id === 'VOICEMAIL_DETECTION'
                        const handleToggle = (checked: boolean) => {
                          const current = field.value ?? []
                          field.onChange(
                            checked
                              ? [...current, tool.id]
                              : current.filter((id: string) => id !== tool.id),
                          )
                        }

                        if (isVoicemail) {
                          return (
                            <div
                              key={tool.id}
                              className={cn(
                                'rounded-md border transition-all duration-150',
                                enabled
                                  ? 'bg-background border-border shadow-sm'
                                  : 'bg-muted/30 border-border/50',
                              )}
                            >
                              <div className="flex items-center gap-3 px-3 py-2.5">
                                <div className="flex-1 min-w-0">
                                  <p
                                    className={cn(
                                      'text-sm font-medium',
                                      !enabled && 'text-muted-foreground',
                                    )}
                                  >
                                    {tool.label}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {tool.description}
                                  </p>
                                </div>
                                <Switch
                                  checked={enabled}
                                  onCheckedChange={handleToggle}
                                  aria-label={`Enable ${tool.label}`}
                                />
                              </div>

                              {enabled && (
                                <div className="px-3 pb-3 pt-3 space-y-3 border-t bg-muted/40">
                                  <FormField
                                    control={control}
                                    name="leaveVoicemailEnabled"
                                    render={({ field: leaveField }) => (
                                      <div className="flex items-center justify-between gap-3">
                                        <div>
                                          <p className="text-sm font-medium">
                                            Leave a message
                                          </p>
                                          <p className="text-xs text-muted-foreground">
                                            Leave a message when a voicemail is
                                            detected.
                                          </p>
                                        </div>
                                        <Switch
                                          checked={leaveField.value ?? true}
                                          onCheckedChange={leaveField.onChange}
                                          aria-label="Enable leaving a voicemail message"
                                        />
                                      </div>
                                    )}
                                  />
                                  {(watch('leaveVoicemailEnabled') ?? true) && (
                                    <div className="space-y-1.5">
                                      <p className="text-xs font-medium">
                                        Message
                                      </p>
                                      <FormField
                                        control={control}
                                        name="voicemailMessage"
                                        render={({ field: vmField }) => (
                                          <VariableAutocompleteTextarea
                                            className="text-sm bg-background min-h-[72px]"
                                            rows={3}
                                            placeholder="Hi, this is {{agentName}} with {{companyName}}. It looks like now isn't the best time to connect, so please give us a callback whenever you're ready!"
                                            value={vmField.value ?? ''}
                                            onChange={vmField.onChange}
                                          />
                                        )}
                                      />
                                      <p className="text-xs text-muted-foreground">
                                        Type {'{{'} to insert a variable. Leave
                                        blank to use the default.
                                      </p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )
                        }

                        return (
                          <div
                            key={tool.id}
                            className={cn(
                              'flex items-center gap-3 px-3 py-2.5 rounded-md border transition-all duration-150',
                              enabled
                                ? 'bg-background border-border shadow-sm'
                                : 'bg-muted/30 border-border/50',
                            )}
                          >
                            <div className="flex-1 min-w-0">
                              <p
                                className={cn(
                                  'text-sm font-medium',
                                  !enabled && 'text-muted-foreground',
                                )}
                              >
                                {tool.label}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {tool.description}
                              </p>
                            </div>
                            <Switch
                              checked={enabled}
                              onCheckedChange={handleToggle}
                              aria-label={`Enable ${tool.label}`}
                            />
                          </div>
                        )
                      })}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Skills                                                            */}
        {/* ---------------------------------------------------------------- */}
        {showSkills && (
          <>
            {showTools && <Separator />}
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Skills
              </p>
              <div className="space-y-2">
                {visibleSkills.map(skill => {
                  const entry = getSkillEntry(skill.type)
                  const isEnabled = entry !== null
                  const isExpanded = isEnabled && expandedSkills.has(skill.type)
                  const params = entry?.params ?? {}

                  return (
                    <Collapsible
                      key={skill.type}
                      open={isExpanded}
                      onOpenChange={() => {
                        if (!isEnabled) {
                          // First click: enable + expand in one gesture
                          handleToggleSkill(skill.type, false)
                        } else {
                          handleToggleExpanded(skill.type)
                        }
                      }}
                    >
                      <div
                        className={cn(
                          'rounded-md border transition-all duration-150',
                          isEnabled
                            ? 'bg-background border-border shadow-sm'
                            : 'bg-muted/30 border-border/50',
                        )}
                      >
                        <CollapsibleTrigger asChild>
                          <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer">
                            <div className="flex-1 min-w-0">
                              <p
                                className={cn(
                                  'text-sm font-medium',
                                  !isEnabled && 'text-muted-foreground',
                                )}
                              >
                                {skill.label}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {skill.description}
                              </p>
                            </div>
                            <ChevronDown
                              className={cn(
                                'h-3.5 w-3.5 text-muted-foreground/50 transition-transform duration-200 shrink-0',
                                isExpanded && 'rotate-180',
                                isEnabled && 'text-muted-foreground',
                              )}
                            />
                            <Switch
                              checked={isEnabled}
                              onCheckedChange={() =>
                                handleToggleSkill(skill.type, isEnabled)
                              }
                              onClick={e => e.stopPropagation()}
                              aria-label={`Enable ${skill.label}`}
                            />
                          </div>
                        </CollapsibleTrigger>

                        <CollapsibleContent>
                          <div className="px-3 pb-4 pt-3 space-y-4 border-t">
                            {skill.type === 'SCHEDULING' && (
                              <SchedulingConfig
                                params={params}
                                onChange={(key, val) =>
                                  updateSkillParam(skill.type, key, val)
                                }
                              />
                            )}
                            {skill.type === 'OWNER_MEETING_SCHEDULING' && (
                              <OwnerMeetingSchedulingConfig
                                params={params}
                                onChange={(key, val) =>
                                  updateSkillParam(skill.type, key, val)
                                }
                              />
                            )}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  )
                })}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Scheduling skill config
// ---------------------------------------------------------------------------
function SchedulingConfig({
  params,
  onChange,
}: {
  params: Record<string, unknown>
  onChange: (key: string, value: unknown) => void
}) {
  const selected = (params.strategy as string) ?? 'FIRST_AVAILABLE'
  const selectedOption = SCHEDULING_STRATEGY_OPTIONS.find(
    o => o.value === selected,
  )

  return (
    <>
      <div className="space-y-2">
        <p className="text-xs font-medium">Strategy</p>
        <div className="flex flex-wrap gap-2">
          {SCHEDULING_STRATEGY_OPTIONS.map(opt => (
            <SelectableBox
              key={opt.value}
              id={opt.value}
              selected={selected === opt.value}
              onSelect={id => onChange('strategy', id)}
            >
              <span>{opt.label}</span>
            </SelectableBox>
          ))}
        </div>
        {selectedOption && (
          <p className="text-xs text-muted-foreground">
            {selectedOption.description}
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <p className="text-xs font-medium">
          Backup link{' '}
          <span className="font-normal text-muted-foreground">(optional)</span>
        </p>
        <Input
          className="h-8 text-sm"
          placeholder="https://showmojo.com/..."
          value={(params.backupLink as string) ?? ''}
          onChange={e => onChange('backupLink', e.target.value || null)}
        />
        <p className="text-xs text-muted-foreground">
          Offered to callers when no availability is found
        </p>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Owner meeting scheduling skill config
// ---------------------------------------------------------------------------
function OwnerMeetingSchedulingConfig({
  params,
  onChange,
}: {
  params: Record<string, unknown>
  onChange: (key: string, value: unknown) => void
}) {
  // Opaque scheduling key stored verbatim — the vendor decides how to interpret
  // it (link vs. path is normalized at the provider boundary, not here).
  const value = (params.meetingLinkSlug as string) ?? ''

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium">
        Default meeting link{' '}
        <span className="font-normal text-muted-foreground">(optional)</span>
      </p>
      <Input
        className="h-8 text-sm"
        placeholder="https://meetings.hubspot.com/owner/meeting-name"
        value={value}
        onChange={e => onChange('meetingLinkSlug', e.target.value || null)}
      />
      <p className="text-xs text-muted-foreground">
        The agent books here by default. Paste the full booking page link (not
        just the path) so it can also be texted to callers when no time works.
      </p>
    </div>
  )
}
